import { HttpClient, HttpErrorResponse, HttpEvent, HttpRequest, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';

import { CameraModel } from 'src/app/models/camera.model';
import { CommandModel, DeviceModel, GatewayModel } from 'src/app/models/device.model';
import { EventModel, RecordModel, DateModel } from 'src/app/models/record.model';
import { DeviceShared } from 'src/app/models/deviceShared.model';
import { DownloadTaskModel } from 'src/app/models/downloadTask.model';

import { environment } from 'src/environments/environment';
import { HttpHeadersHelper } from 'src/app/helpers/headers';

import { AuthService } from 'src/app/services/auth.service';
import { UserService } from 'src/app/services/user.service';
import { AlertService } from 'src/app/services/alert.service';

import { Directory, Filesystem } from '@capacitor/filesystem';
import write_blob from "capacitor-blob-writer";
import { FileOpener } from '@ionic-native/file-opener/ngx';


@Injectable({
  providedIn: 'root'
})
export class DeviceService {

  protected apiUrl = environment.apiUrl;
  public    cameras:        CameraModel[]  = [];
  public    sharedCameras:  CameraModel[]  = [];
  public    devices:        DeviceModel[]  = [];
  public    sharedDevices:  DeviceModel[]  = [];
  public    gateways:       GatewayModel[] = [];
  protected previews:       { [param: number]: string; } = {};

  public    isFileLoading = false;

  protected sensorEventsDesc = [
    {
      type:   'flood',
      sensor: 'Flood',
      full:   {'on': 'Обнаружена протечка воды', 'off': 'Протечка воды прекратилась'},
      short:  {'on': 'Есть', 'off': 'Нет'}
    },
    {
      type:   'multi',
      sensor: 'Burglar',
      full:   {'on': 'Обнаружено движение', 'off': 'Движение прекратилось'},
      short:  {'on': 'Есть', 'off': 'Нет'}
    },
    {
      type:   'multi',
      sensor: 'Access_Control',
      full:   {'on': 'Открыто', 'off': 'Закрыто'},
      short:  {'on': 'Открыто', 'off': 'Закрыто'}
    },
    {
      type:   'smoke',
      sensor: 'Smoke',
      full:   {'on': 'Обнаружено задымление', 'off': 'Задымление прекратилось'},
      short:  {'on': 'Есть', 'off': 'Нет'}
    },
    {
      type:   'smoke',
      sensor: 'Burglar',
      full:   {'on': 'Обнаружено вскрытие корпуса'},
      short:  {'on': 'Открыто', 'off': 'Закрыто'}
    },
    {
      type:   'plug',
      sensor: 'Switch',
      full:   {'on': 'Розетка включена', 'off': 'Розетка выключена'},
      short:  {'on': 'Вкл.', 'off': 'Выкл.'}
    },
  ];

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    private user: UserService,
    private alert: AlertService,
    private platform: Platform,
    private fileOpener: FileOpener
  ) {}

  public clear() {
    this.cameras        = [];
    this.sharedCameras  = [];
    this.devices        = [];
    this.sharedDevices  = [];
    this.gateways       = [];
    this.previews       = [];
  }

  public async getDevices(groupId: number = 0): Promise<CameraModel[]> {
    // console.log('getDevices(): Entering');
    const token = await this.auth.getToken();

    return new Promise<CameraModel[]>((resolve, reject) => {
      this.http.get(this.apiUrl + '/devices/' + groupId, {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then((result: {success: {
          cameras:  CameraModel[],
          devices:  DeviceModel[],
          gateways: GatewayModel[]
        }}) => {
        this.cameras  = result.success.cameras;
        this.devices  = result.success.devices;
        this.gateways = result.success.gateways;
        // console.log('getDevices():cameras: ', this.cameras);
        // console.log('getDevices():devices: ', this.devices);
        // console.log('getDevices():gateways: ', this.gateways);
        resolve(this.cameras);
      })
      .catch((err: HttpErrorResponse) => {
        console.error('getDevices(): ', err);
        reject(err);
      });
    });
  }

  public getCamerasByFolder(folderId: number) {
    return this.cameras.filter((camera: CameraModel) => {
      return camera.group_id === folderId;
    });
  }

  public getDevicesByFolder(folderId: number) {
    return this.devices.filter((device: DeviceModel) => {
      return device.group_id === folderId;
    });
  }

  public getGatewaysByFolder(folderId: number) {
    return this.gateways.filter((gateway: GatewayModel) => {
      return gateway.group_id === folderId;
    });
  }

  public async getCamera(id: number): Promise<CameraModel> {
    const token = await this.auth.getToken();

    return new Promise<CameraModel>((resolve, reject) => {
      this.http.get(this.apiUrl + '/camera/' + id, {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then((result: { success: CameraModel }) => {
        resolve(result.success);
      })
      .catch((err: HttpErrorResponse) => {
        console.error('getCamera(): ', err);
        reject(err);
      });
    });
  }

  public async getDevice(id: number): Promise<DeviceModel> {
    const token = await this.auth.getToken();

    return new Promise<DeviceModel>((resolve, reject) => {
      this.http.get(this.apiUrl + '/device/' + id, {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then((result: { success: DeviceModel }) => {
        resolve(result.success);
      })
      .catch((err: HttpErrorResponse) => {
        console.error('getDevice(): ', err);
        reject(err);
      });
    });
  }

  public async saveCamera(camera: CameraModel): Promise<string | void> {
    const token = await this.auth.getToken();

    return new Promise<string | void>((resolve, reject) => {
      this.http.post(this.apiUrl + '/camera/' + camera.id, camera, {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then((result: { success: boolean, id: number }) => {
        const storedCamera = this.cameras.find(cam => cam.id === result.id);
        if (storedCamera) {
          storedCamera.title        = camera.title;
          storedCamera.group_id     = camera.group_id;
          storedCamera.recording    = camera.recording;
          storedCamera.schedule_id  = camera.schedule_id;
          storedCamera.notices      = camera.notices;
        } else {
          camera.id = result.id;
          this.cameras.push(camera);
        }
        resolve();
      })
      .catch((err: HttpErrorResponse) => {
        console.error('saveCamera(): ', err);
        reject(err.error.error);
      });
    });
  }

  public async saveDevice(device: DeviceModel): Promise<string | void> {
    const token = await this.auth.getToken();

    return new Promise<string | void>((resolve, reject) => {
      this.http.post(this.apiUrl + '/device/' + device.id, device, {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then((result: { success: boolean, id: number }) => {
        const storedDevice = this.devices.find(dev => dev.id === result.id);
        if (storedDevice) {
          storedDevice.title        = device.title;
          storedDevice.group_id     = device.group_id;
        }
        resolve();
      })
      .catch((err: HttpErrorResponse) => {
        console.error('saveDevice(): ', err);
        reject(err.error.error);
      });
    });
  }

  public async sendDeviceCommand(device: DeviceModel, command: CommandModel): Promise<string | void> {
    const token = await this.auth.getToken();

    return new Promise<string | void>((resolve, reject) => {
      this.http.post(this.apiUrl + '/device/command/' + device.id, {
        deviceId:   device.device_id,
        gatewayId:  device.gateway_id,
        command:    command.command,
        attribute:  command.attribute
      }, {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then((result: { success: boolean }) => {
        resolve();
      })
      .catch((err: HttpErrorResponse) => {
        console.error('sendDeviceCommand(): ', err);
        reject(err.error.error);
      });
    });
  }

  public async deleteCamera(cameraId: number): Promise<string | void> {
    const token = await this.auth.getToken();

    return new Promise<string | void>((resolve, reject) => {
      this.http.delete(this.apiUrl + '/camera/' + cameraId, {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then((result: { success: boolean }) => {
        const storedCamera = this.cameras.find(cam => cam.id === cameraId);
        if (storedCamera) {
          this.cameras.splice(this.cameras.indexOf(storedCamera), 1);
        }
        resolve();
      })
      .catch((err: HttpErrorResponse) => {
        console.error('deleteCamera(): ', err);
        reject(err);
      });
    });
  }

  public async getDeviceNotifications(model: string): Promise<string[]> {
    const token = await this.auth.getToken();

    return new Promise<string[]>((resolve, reject) => {
      this.http.get(this.apiUrl + '/device/notice/' + model, {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then((result: { success: string[] }) => {
        resolve(result.success);
      })
      .catch((err: HttpErrorResponse) => {
        console.error('getDeviceNotifications(): ', err);
        reject(err);
      });
    });
  }

  public async getCameraPreviews(idList: number[]):
    Promise<{ [param: number]: string; }> {
      const token = await this.auth.getToken();

      return new Promise<{ [param: number]: string; }>((resolve, reject) => {
        this.http.get(this.apiUrl + '/camera/previews/' + idList.join(':'), {
          headers: HttpHeadersHelper.createHeaders(token),
          withCredentials: false
        })
        .toPromise()
        .then((result: { success: { [param: number]: string; } }) => {
          // console.log('getCameraPreviews(): ', result.success);
          this.previews = result.success;
          resolve(result.success);
        })
        .catch((err: HttpErrorResponse) => {
          console.error('getCameraPreviews(): ', err);
          reject(err);
        });
      });
  }

  public loadPreviews() {
    let ids = this.cameras.map((device: CameraModel) => device.id);
    ids = ids.concat(this.sharedCameras.map((device: CameraModel) => device.id));

    if (!ids.length) {
      console.error('loadPreviews(): Cameras or shared devices array is empty');
      return;
    }

    return this.getCameraPreviews(ids);
  }

  public getPreview(id: number) {
    if (id in this.previews) {
        return this.previews[id];
    }
  }

  public async getRecordModes(isNewCamera: boolean = true) {
    let recordModes = [];
    if (this.user.userInfo.tfid <= 100) {
      recordModes = [
        { name: 'continuous', description: 'Постоянная' },
      ];
      if (isNewCamera) {
        recordModes.push({ name: 'motion', description: 'По детектору движения' });
      }
    } else {
      recordModes = [
        { name: 'motion',     description: 'По детектору движения' },
      ];
    }
    return recordModes;
  }

  public async getSharedCameras(subsidiaryId: number) {
    // console.log('getSharedCameras(): Entering', subsidiaryId);
    const token = await this.auth.getToken();

    await this.http.get(this.apiUrl + '/subsidiary/cameras', {
      headers: HttpHeadersHelper.createHeaders(token),
      params: {subsidiaryId: String(subsidiaryId)},
      withCredentials: false
    })
    .toPromise()
    .then((result: {success: boolean, cameras: CameraModel[]}) => {
      this.sharedCameras = result.cameras;
      // console.log('getSharedCameras(): ', this.sharedCameras);
    })
    .catch((err: HttpErrorResponse) => {
      console.error('getSharedCameras(): ', err);
      // reject(err);
    });

    return this.sharedCameras;
  }

  public async getSharedSensors(subsidiaryId: number) {
    // console.log('getSharedSensors(): Entering', subsidiaryId);
    const token = await this.auth.getToken();

    await this.http.get(this.apiUrl + '/subsidiary/sensors', {
      headers: HttpHeadersHelper.createHeaders(token),
      params: {subsidiaryId: String(subsidiaryId)},
      withCredentials: false
    })
    .toPromise()
    .then((result: {success: boolean, sensors: DeviceModel[]}) => {
      this.sharedDevices = result.sensors;
      // console.log('getSharedSensors(): ', this.sharedDevices);
    })
    .catch((err: HttpErrorResponse) => {
      console.error('getSharedSensors(): ', err);
      // reject(err);
    });

    return this.sharedCameras;
  }

  public async getDeviceSharedAccounts(deviceId: string, deviceType: string) {
    // console.log('getDeviceSharedAccounts(): Entering');
    const token = await this.auth.getToken();
    let sharedAccounts: DeviceShared[];

    // if (!this.sharedCameras.length) {
      await this.http.get(this.apiUrl + '/subsidiary/accounts/device', {
        headers: HttpHeadersHelper.createHeaders(token),
        params: {
          deviceId:   deviceId,
          deviceType: deviceType
        },
        withCredentials: false
      })
      .toPromise()
      .then((result: {success: boolean, accounts: DeviceShared[]}) => {
        sharedAccounts = result.accounts;
        // console.log('getDeviceSharedAccounts(): ', sharedAccounts);
      })
      .catch((err: HttpErrorResponse) => {
        console.error('getDeviceSharedAccounts(): ', err);
        // reject(err);
      });
    // }

    return sharedAccounts;
  }

  public async setSharedCameras(streamId: string, subsidiaryId: number, isSet: boolean) {
    // console.log('setSharedCameras(): Entering');
    const token = await this.auth.getToken();

    await this.http.post(this.apiUrl + '/subsidiary/camera', {
      streamId: streamId,
      subsidiaryId: subsidiaryId,
      isSet: isSet
    }, {
      headers: HttpHeadersHelper.createHeaders(token),
      withCredentials: false
    })
    .toPromise()
    .catch((err: HttpErrorResponse) => {
      console.error('setSharedCameras(): ', err);
      // reject(err);
    });
  }

  public async setSharedSensors(deviceId: string, subsidiaryId: number, isSet: boolean) {
    // console.log('setSharedSensors(): Entering');
    const token = await this.auth.getToken();

    await this.http.post(this.apiUrl + '/subsidiary/sensor', {
      deviceId:     deviceId,
      subsidiaryId: subsidiaryId,
      isSet:        isSet
    }, {
      headers: HttpHeadersHelper.createHeaders(token),
      withCredentials: false
    })
    .toPromise()
    .catch((err: HttpErrorResponse) => {
      console.error('setSharedSensors(): ', err);
      // reject(err);
    });
  }

  public async getCameraRecords(cameraId: number, date: string) {
    // console.log('getCameraRecords(): Entering ', cameraId, date);
    const token = await this.auth.getToken();
    let records: RecordModel[];
    let events:  EventModel[];

    await this.http.get(this.apiUrl + '/camera/records', {
      headers: HttpHeadersHelper.createHeaders(token),
      params: {cameraId: String(cameraId), date: date},
      withCredentials: false
    })
    .toPromise()
    .then((result: {success: boolean, records: RecordModel[], events: EventModel[]}) => {
      records = result.records;
      events  = result.events;
      // console.log('getCameraRecords(): ', records, events);
    })
    .catch((err: HttpErrorResponse) => {
      console.error('getCameraRecords(): ', err);
      // reject(err);
    });

    return {records: records, events: events};
  }

  public async getCameraDates(cameraId: number) {
    const token = await this.auth.getToken();
    let   dates: string[];

    await this.http.get(this.apiUrl + '/camera/dates', {
      headers: HttpHeadersHelper.createHeaders(token),
      params: {cameraId: String(cameraId)},
      withCredentials: false
    })
    .toPromise()
    .then((result: {success: boolean, dates: string[]}) => {
      dates = result.dates;
      // console.log('getCameraDates(): ', dates);
    })
    .catch((err: HttpErrorResponse) => {
      console.error('getCameraDates(): ', err);
      // reject(err);
    });

    return {dates: dates};
  }

  public async createDownloadTask(cameraId: number, startTime: Date, stopTime: Date, doCropping: number) {
    // console.log('createDownloadTask(): Entering');
    const token = await this.auth.getToken();

    await this.http.post(this.apiUrl + '/camera/records/download', {
      cameraId:     cameraId,
      startTime:    startTime,
      stopTime:     stopTime,
      doCropping:   doCropping,
    }, {
      headers: HttpHeadersHelper.createHeaders(token),
      withCredentials: false
    })
    .toPromise()
    .catch((err: HttpErrorResponse) => {
      console.error('createDownloadTask(): ', err);
      // reject(err);
    });
  }

  public async getDownloadTasks(cameraId: number) {
    // console.log('getDownloadTasks(): Entering ', cameraId);
    const token = await this.auth.getToken();
    let tasks: DownloadTaskModel[];

    await this.http.get(this.apiUrl + '/camera/records/download/' + cameraId, {
      headers: HttpHeadersHelper.createHeaders(token),
      params: {
        cameraId:     String(cameraId),
      },
      withCredentials: false
    })
    .toPromise()
    .then((result: {success: boolean, tasks: DownloadTaskModel[]}) => {
      tasks = result.tasks;
      // console.log('getDownloadTasks(): ', tasks);
    })
    .catch((err: HttpErrorResponse) => {
      console.error('getDownloadTasks(): ', err);
      // reject(err);
    });

    return tasks;
  }

  public saveBlobFromBrowser(blob: any, fileName: string) {
    // IE doesn't allow using a blob object directly as link href
    // instead it is necessary to use msSaveOrOpenBlob
    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(blob);
      return;
    }

    // For other browsers:
    // Create a link pointing to the ObjectURL containing the blob.
    const data = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = data;
    link.download = fileName;
    // this is necessary as link.click() does not work on the latest firefox
    link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));

    setTimeout(function () {
      // For Firefox it is necessary to delay revoking the ObjectURL
      window.URL.revokeObjectURL(data);
      link.remove();
    }, 100);

    this.alert.presentToast('Файл сохранен в папке "Загрузки" (Downloads)');
  }

  public async saveBlobFromMobile(blob: any, fileName: string, mimeType: string = 'image/jpeg') {
    let toastMessage  = 'Файл сохранен в папке "Документы" (Document)';
    console.log('saveBlobFromMobile():',  mimeType);
    write_blob({
      path: fileName,
      blob: blob,
      directory: Directory.Documents,
    }).then((file) => {
      this.alert.presentToast(toastMessage);
      // Open file in iOS
      if (this.platform.is('ios')) {
        console.log('IOS !!!')
        Filesystem.getUri({path: fileName, directory: Directory.Documents}).then(
          (savedFile) => {
            this.fileOpener.showOpenWithDialog(savedFile.uri, mimeType)
            .then(() => console.log('File is opened'))
            .catch(e => console.log('Error opening file', e));
          })
          }
    }
    ).catch((error) => {
      console.log('saveBlobFromMobile(): File write failed:', error);
      this.alert.presentToast('Ошибка записи файла', 'danger');
    });
  }

  public async downloadFile(task: DownloadTaskModel) {
    // console.log('downloadFile():');

    const req = new HttpRequest('GET', task.download_url, {
      headers: HttpHeadersHelper.createHeaders(this.auth.token),
      reportProgress: true,
      responseType: 'blob',
      withCredentials: false
    });

    await this.http.request(req)
    .subscribe((event: HttpEvent<any>) => {
      switch (event.type) {
        case HttpEventType.Sent:
            // console.log('downloadFile(): Request sent!');
            this.isFileLoading = true;
            break;
        case HttpEventType.ResponseHeader:
            // console.log('downloadFile(): Response header received!');
            break;
        case HttpEventType.DownloadProgress:
            const progress = Math.trunc(event.loaded / event.total * 100);
            // console.log('downloadFile(): Progress:', progress);
            break;
        case HttpEventType.Response:
            // console.log('downloadFile(): 😺 Done!');
            const mimeType = 'video/mp4';
            if (this.platform.is('desktop')) {
              // Desktop browser device
              const newBlob = new Blob([event.body], { type: mimeType });
              this.saveBlobFromBrowser(newBlob, task.publish_file);
              this.isFileLoading = false;

            } else if (this.platform.is('mobile')) {
              // Mobile device
              this.saveBlobFromMobile(event.body, task.publish_file, mimeType);
              this.isFileLoading = false;
            }
            break;
      }
    });
  }

  public async getDeviceHistory(deviceId: number, date: string) {
    // console.log('getDeviceHistory(): Entering ', deviceId, date);
    const token = await this.auth.getToken();
    let history: any[];

    await this.http.get(this.apiUrl + '/device/history/' + String(deviceId), {
      headers: HttpHeadersHelper.createHeaders(token),
      params: {
        deviceId: String(deviceId),
        date:     date,
      },
      withCredentials: false
    })
    .toPromise()
    .then((result: {success: boolean, history: any[]}) => {
      history = result.history;
      // console.log('getDeviceHistory(): ', history);
    })
    .catch((err: HttpErrorResponse) => {
      console.error('getDeviceHistory(): ', err);
      // reject(err);
    });

    return history;
  }

  public async getDeviceStatistics(deviceId: number, sensorType: string, period: any) {
    // console.log('getDeviceStatistics(): Entering ', deviceId, sensorType, period);
    const token = await this.auth.getToken();
    let statistics: any[];

    await this.http.get(this.apiUrl + '/device/statistics/' + String(deviceId), {
      headers: HttpHeadersHelper.createHeaders(token),
      params: {
        deviceId:     String(deviceId),
        sensorType:   sensorType,
        periodName:   period.name,
        periodBegin:  period.begin.toISOString(),
        periodEnd:    period.end.toISOString(),
      },
      withCredentials: false
    })
    .toPromise()
    .then((result: {success: boolean, statistics: any[]}) => {
      statistics = result.statistics;
      // console.log('getDeviceStatistics(): ', statistics);
    })
    .catch((err: HttpErrorResponse) => {
      console.error('getDeviceStatistics(): ', err);
      // reject(err);
    });

    return statistics;
  }

  public async addGateway(qrCode: any): Promise<string | void> {
    // console.log('addGateway(): Entering ', deviceId, sensorType, date, period);
    const token = await this.auth.getToken();

    return new Promise<string | void>(async (resolve, reject) => {
      await this.http.post(this.apiUrl + '/device/gateway/add', {
        vendor:       qrCode.vendor,
        serialNumber: qrCode.serialNumber,
        gatewayId:    qrCode.gatewayId
      }, {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then(() => {
        resolve();
      })
      .catch((err: HttpErrorResponse) => {
        console.error('addGateway(): ', err);
        reject(err.error.error);
      });
    });
  }

  public async updateGateway(gatewayId: number): Promise<string | void> {
    // console.log('updateGateway(): Entering ');
    const token = await this.auth.getToken();

    return new Promise<string | void>(async (resolve, reject) => {
      await this.http.post(this.apiUrl + '/device/gateway/update/' + gatewayId, {},
      {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then(() => {
        resolve();
      })
      .catch((err: HttpErrorResponse) => {
        console.error('updateGateway(): ', err);
        reject(err.error.error);
      });
    });
  }

  public async removeGateway(gatewayId: number): Promise<string | void> {
    // console.log('removeGateway(): Entering ');
    const token = await this.auth.getToken();

    return new Promise<string | void>(async (resolve, reject) => {
      await this.http.delete(this.apiUrl + '/device/gateway/' + gatewayId,
      {
        headers: HttpHeadersHelper.createHeaders(token),
        withCredentials: false
      })
      .toPromise()
      .then(() => {
        resolve();
      })
      .catch((err: HttpErrorResponse) => {
        console.error('removeGateway(): ', err);
        reject(err.error.error);
      });
    });
  }

  public getSensorEventDesc(type: string, sensor: string, event: string, form: string, updatedAt: string) {

    // Return time for motion detector
    if (type === 'multi' && sensor === 'Burglar' && updatedAt !== undefined) {
      return updatedAt.split(' ')[1];
    }

    const desc = this.sensorEventsDesc.find(dsc => dsc.type === type && dsc.sensor === sensor);
    if (desc === undefined) {
      return event;
    }
    return desc[form][event];
  }

  public async getAllDevicesStatuses() {
    const token = await this.auth.getToken();

    await this.http.get(this.apiUrl + '/statuses', {
      headers: HttpHeadersHelper.createHeaders(token),
      withCredentials: false
    })
    .toPromise()
    .then((result: {success: boolean, statuses: any}) => {
      // console.log('getAllDevicesStatuses(): ', result.statuses);

      result.statuses.cameras.forEach((status: any) => {
        const camera = this.cameras.find(cam => cam.stream_id === status.stream_id);
        if (camera !== undefined) {
          camera.onair = status.onair;
        }
      });

      result.statuses.devices.forEach((status: any) => {
        const device = this.devices.find(dev => dev.device_id === status.device_id);
        if (device !== undefined) {
          device.state = status.state;
        }
      });

      result.statuses.gateways.forEach((status: any) => {
        const gateway = this.gateways.find(gw => gw.gateway_id === status.gateway_id);
        if (gateway !== undefined) {
          gateway.status = status.status;
        }
      });

    })
    .catch((err: HttpErrorResponse) => {
      console.error('getAllDevicesStatuses(): ', err);
    });
  }
}
